import { Card, CardContent, Search, Table } from '@ibsys/kit';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { KNOWLEDGE_SOURCE_FILTER_PARAM } from '@/entities/knowledge';
import { useTableState } from '@/entities/settings';
import { useAppNavigate } from '@/shared/lib/ts-router';
import { PageContainer } from '@/shared/ui';
import { readFileNameFilter, writeFileNameFilter } from '../model/fileNameFilter';
import { useKnowledgeSourceColumns } from '../model/useKnowledgeSourceColumns';
import { useKnowledgeSourcesFetch } from '../model/useKnowledgeSourcesFetch';

export const KnowledgeSourcesPage: FC = () => {
  const { t } = useTranslation(['knowledgeSources', 'common']);

  const fetchData = useKnowledgeSourcesFetch();
  const { settings, actions, path } = useTableState('knowledgeSourcesList');
  const navigate = useAppNavigate();

  const handleGoToNodes = useCallback(
    (sourceId: number) => {
      navigate('/knowledge', undefined, { query: { [KNOWLEDGE_SOURCE_FILTER_PARAM]: sourceId } });
    },
    [navigate],
  );

  const columns = useKnowledgeSourceColumns({ onGoToNodes: handleGoToNodes });

  const handleSearchChange = useCallback(
    (fileName: string) => {
      actions.onFiltersChange((prev) => writeFileNameFilter(prev, fileName.trim()));
    },
    [actions],
  );

  return (
    // Содержимое раздела по краю не обрезается: иначе край срезал бы выделение поля поиска,
    // которое рисуется наружу от его границ. Список в свои пределы укладывает карточка ниже
    <PageContainer enableScroll={false} className="flex h-full w-full flex-col gap-4 pb-4">
      <header className="flex w-full items-center gap-4">
        <Search
          className="w-full"
          placeholder={t('knowledgeSources:filters.search')}
          value={readFileNameFilter(settings.filters)}
          onChange={(event) => handleSearchChange(event.target.value)}
        />
      </header>
      <Card className="flex flex-1 flex-col gap-0 overflow-hidden">
        <CardContent className="h-full flex-1 overflow-hidden rounded-xl p-0">
          <Table
            type="lazy"
            limit={50}
            fetchData={fetchData}
            dataKey="items"
            virtualization={{ enable: true, rowHeight: 40 }}
            styles={{
              size: 'sm',
            }}
            classNames={{
              header: '[&_th]:h-14',
              table: 'min-w-full',
            }}
            className="h-full w-full border-0"
            emptyMessage={t('knowledgeSources:noFiles')}
            enableMultiSort={false}
            {...settings}
            {...actions}
            tableStateStoragePath={`${path}.ordering`}
            enableColumnOrdering
            enableColumnSizing
            initialOffset={undefined}
            columns={columns}
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
};
